import { ListingPhoto, PhotoThumbnail, PhotoThumbnails, ThumbnailSizes } from '@lc/core';
import { LoadingEvent } from '@lc/ui';
import { DragItem } from '../layout/drag-n-drop/drag-n-drop.component';

/**
 * The Photo View Model is used by this component to handle the coordination of events related to a specific photo
 * Each photo in the grid has many actions we need to keep track of. In order to do so, we need to directly correlate the
 * photo with isSelected and isEditNumber. This also will make it easier to maintain rather than having separate lookup
 * objects to maintain these values.
 */
export class PhotoViewModel implements DragItem {
  private static idCounter = 0;
  id: number;
  isSelected: boolean;
  isEditNumber: boolean;
  canFavorite: boolean;
  canReorder: boolean;
  canDownload: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canSelect: boolean;
  canHide: boolean;
  inUseIndex?: number;
  showOrder: boolean;
  showFavorite: boolean;
  useSmallIcons: boolean;
  iconPlacement: 'default' | 'center' | 'corner' = 'default';
  showSelected: boolean;
  pageDisplay: string;
  disablePortrait?: boolean;
  tooltip?: string;
  disabled?: boolean;
  isRequired: boolean;
  index: number;

  loading?: LoadingEvent;

  constructor(public photo: ListingPhoto) {
    this.id = PhotoViewModel.idCounter;
    PhotoViewModel.idCounter += 1;
  }

  isDefault() {
    if (!(this.photo && this.photo.uri)) {
      return true;
    }
    return this.photo.uri.indexOf('welcome-house.png') >= 0;
  }

  /**
   * Builds a view model for a product photo grid/picker: non-default photos are deletable/editable
   * and get a corner icon placement, and the photo is marked required if it's within the first
   * `requiredPhotoCount` photos.
   */
  static createForProductPhoto(photo: ListingPhoto, index: number, requiredPhotoCount: number): PhotoViewModel {
    const viewModel = new PhotoViewModel(photo);
    const isDefault = viewModel.isDefault();
    viewModel.canFavorite = false;
    viewModel.canReorder = true;
    viewModel.disabled = isDefault;
    viewModel.disablePortrait = false;
    viewModel.canDownload = false;
    viewModel.canSelect = false;
    viewModel.isRequired = index < requiredPhotoCount;
    viewModel.canDelete = !isDefault;
    viewModel.canEdit = !isDefault;
    viewModel.iconPlacement = isDefault ? 'default' : 'corner';

    return viewModel;
  }
}

export const buildPlaceholderImage = (index: number): ListingPhoto => {
  const placeholder = new ListingPhoto();
  const placeholderUri = 'assets/images/welcome-house.png';
  placeholder.favorite = false;
  placeholder.uri = placeholderUri;
  placeholder.order = index + 1;
  placeholder.uploadedBy = 'placeholder';
  placeholder.dimensions = {};
  placeholder.thumbnails = new PhotoThumbnails();
  placeholder.thumbnails[ThumbnailSizes.MEDIUM_THUMBNAIL_WIDTH] = new PhotoThumbnail({ uri: placeholderUri });
  placeholder.thumbnails[ThumbnailSizes.LARGE_THUMBNAIL_WIDTH] = new PhotoThumbnail({ uri: placeholderUri });

  return placeholder;
};
